import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import {
  closePanel,
  openPanel,
  type AnalysisPanelTab,
  type AnalysisPanelSource,
} from '../store/slices/analysisPanelSlice';
import {
  clearDeepAnalysisReturnTab,
  setDeepAnalysisReturnTab,
  type DeepSubTab,
} from '../store/slices/modalsSlice';
import { type TabId } from '../components/layouts';

type NavTabId = Exclude<TabId, 'trading'>;
type ReturnTab = 'screening' | 'charts';

export interface NavDescriptor {
  v: 3;
  activeTab: NavTabId;
  deepSubTab: DeepSubTab | null;
  panel: {
    ticker: string;
    tab: AnalysisPanelTab;
    source: AnalysisPanelSource;
  } | null;
  returnTab: ReturnTab | null;
}

export const TICKER_SCRUB_REPLACE_MS = 400;
export const NAV_DESC_SCHEMA_VERSION = 3;

const VALID_TAB_IDS = new Set<NavTabId>([
  'screening',
  'favorites',
  'alerts',
  'positions',
  'help',
  'charts',
  'deep_analysis',
  'admin',
  'agents',
  'roles',
  'settings',
]);
const VALID_PANEL_TABS = new Set<AnalysisPanelTab>([
  'chart',
  'tradingview',
  'ai',
  'details',
  'manual-analysis',
]);
const VALID_SOURCES = new Set<AnalysisPanelSource>(['screening', 'favorites']);
const VALID_RETURN_TABS = new Set<ReturnTab>(['screening', 'charts']);
const VALID_SUB_TABS = new Set<DeepSubTab>(['analysis', 'chart']);
const TICKER_RE = /^[A-Z][A-Z0-9]{0,4}(?:[-.][A-Z0-9]{1,4})?$/;

// V1 schema: { v: 1, activeTab, panel, returnTab } -- legacy `chart_view` and
// `deep_analysis` activeTabs map to `deep_analysis` + corresponding sub-tab.
const LEGACY_V1_TAB_TO_SUB: Record<string, { activeTab: string; deepSubTab: DeepSubTab | null }> = {
  chart_view: { activeTab: 'deep_analysis', deepSubTab: 'chart' },
  deep_analysis: { activeTab: 'deep_analysis', deepSubTab: 'analysis' },
};

// V2 schema: stored chartsSubTab when activeTab === 'charts'. v2 sub-tab
// values were 'grid' | 'chart' | 'deep'; map them to v3 (activeTab,deepSubTab).
const LEGACY_V2_SUB_TO_V3: Record<string, { activeTab: string; deepSubTab: DeepSubTab | null }> = {
  chart: { activeTab: 'deep_analysis', deepSubTab: 'chart' },
  deep: { activeTab: 'deep_analysis', deepSubTab: 'analysis' },
  grid: { activeTab: 'charts', deepSubTab: null },
};

function upcastV1Descriptor(d: Record<string, unknown>): Record<string, unknown> | null {
  if (typeof d.activeTab !== 'string') return null;
  let activeTab = d.activeTab;
  let deepSubTab: DeepSubTab | null = null;

  const mapped = LEGACY_V1_TAB_TO_SUB[activeTab];
  if (mapped) {
    activeTab = mapped.activeTab;
    deepSubTab = mapped.deepSubTab;
  }

  let returnTab = d.returnTab;
  if (returnTab === 'chart_view') returnTab = 'charts';

  return {
    v: 3,
    activeTab,
    deepSubTab,
    panel: d.panel ?? null,
    returnTab,
  };
}

function upcastV2Descriptor(d: Record<string, unknown>): Record<string, unknown> | null {
  if (typeof d.activeTab !== 'string') return null;
  let activeTab = d.activeTab;
  let deepSubTab: DeepSubTab | null = null;

  // v2 only carried a sub-tab when activeTab === 'charts'.
  if (activeTab === 'charts' && typeof d.chartsSubTab === 'string') {
    const mapped = LEGACY_V2_SUB_TO_V3[d.chartsSubTab];
    if (mapped) {
      activeTab = mapped.activeTab;
      deepSubTab = mapped.deepSubTab;
    }
  }

  return {
    v: 3,
    activeTab,
    deepSubTab,
    panel: d.panel ?? null,
    returnTab: d.returnTab ?? null,
  };
}

export function validateDescriptor(raw: unknown): NavDescriptor | null {
  if (!raw || typeof raw !== 'object') return null;
  let d = raw as Record<string, unknown>;

  // Best-effort upcast from v1 / v2 to v3 so users with stale browser history
  // entries can still navigate Back/Forward across them.
  if (d.v === 1) {
    const upcast = upcastV1Descriptor(d);
    if (!upcast) return null;
    d = upcast;
  } else if (d.v === 2) {
    const upcast = upcastV2Descriptor(d);
    if (!upcast) return null;
    d = upcast;
  }

  if (d.v !== NAV_DESC_SCHEMA_VERSION) return null;
  if (typeof d.activeTab !== 'string' || !VALID_TAB_IDS.has(d.activeTab as NavTabId)) return null;

  if (d.deepSubTab != null) {
    if (typeof d.deepSubTab !== 'string' || !VALID_SUB_TABS.has(d.deepSubTab as DeepSubTab)) return null;
  }

  if (d.panel != null) {
    if (typeof d.panel !== 'object') return null;
    const p = d.panel as Record<string, unknown>;
    if (typeof p.ticker !== 'string' || !TICKER_RE.test(p.ticker)) return null;
    if (typeof p.tab !== 'string' || !VALID_PANEL_TABS.has(p.tab as AnalysisPanelTab)) return null;
    if (typeof p.source !== 'string' || !VALID_SOURCES.has(p.source as AnalysisPanelSource)) return null;
  }

  if (d.returnTab != null) {
    if (typeof d.returnTab !== 'string' || !VALID_RETURN_TABS.has(d.returnTab as ReturnTab)) return null;
  }

  return {
    v: 3,
    activeTab: d.activeTab as NavTabId,
    deepSubTab: (d.deepSubTab as DeepSubTab | null) ?? null,
    panel: d.panel
      ? {
          ticker: (d.panel as { ticker: string }).ticker,
          tab: (d.panel as { tab: AnalysisPanelTab }).tab,
          source: (d.panel as { source: AnalysisPanelSource }).source,
        }
      : null,
    returnTab: (d.returnTab as ReturnTab | null) ?? null,
  };
}

export function mapToSafeTab(
  t: NavTabId,
  auth: { isAuthenticated: boolean; isAdmin: boolean }
): NavTabId {
  if (t === 'admin' && !auth.isAdmin) return 'screening';
  if ((t === 'agents' || t === 'roles' || t === 'settings') && !auth.isAuthenticated) return 'screening';
  return t;
}

function descEquals(a: NavDescriptor | null, b: NavDescriptor | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.activeTab !== b.activeTab) return false;
  if (a.deepSubTab !== b.deepSubTab) return false;
  if (a.returnTab !== b.returnTab) return false;
  if (a.panel === b.panel) return true;
  if (!a.panel || !b.panel) return false;
  return (
    a.panel.ticker === b.panel.ticker &&
    a.panel.tab === b.panel.tab &&
    a.panel.source === b.panel.source
  );
}

function isOnlyTickerDiff(cur: NavDescriptor, prev: NavDescriptor | null): boolean {
  if (!prev) return false;
  if (cur.activeTab !== prev.activeTab) return false;
  if (cur.deepSubTab !== prev.deepSubTab) return false;
  if (cur.returnTab !== prev.returnTab) return false;
  if (!cur.panel || !prev.panel) return false;
  if (cur.panel.tab !== prev.panel.tab) return false;
  if (cur.panel.source !== prev.panel.source) return false;
  return cur.panel.ticker !== prev.panel.ticker;
}

export interface UseBrowserNavigationSyncArgs {
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  deepSubTab: DeepSubTab;
  setDeepSubTab: (tab: DeepSubTab) => void;
}

interface LatestState {
  activeTab: TabId;
  deepSubTab: DeepSubTab;
  panel: { isOpen: boolean; selectedTicker: string; activeTab: AnalysisPanelTab; source: AnalysisPanelSource };
  returnTab: ReturnTab | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

export default function useBrowserNavigationSync({
  activeTab,
  setActiveTab,
  isAuthenticated,
  isAdmin,
  deepSubTab,
  setDeepSubTab,
}: UseBrowserNavigationSyncArgs): void {
  const dispatch = useAppDispatch();
  const panel = useSelector((s: RootState) => s.analysisPanel);
  const returnTab = useSelector((s: RootState) => s.modals.deepAnalysisReturnTab);

  const lastAppliedDescRef = useRef<NavDescriptor | null>(null);
  const restoringRef = useRef<{ targetDesc: NavDescriptor } | null>(null);
  const lastPushTimeRef = useRef<number>(0);
  const latestStateRef = useRef<LatestState>({
    activeTab,
    deepSubTab,
    panel,
    returnTab,
    isAuthenticated,
    isAdmin,
  });

  const serialize = useCallback((): NavDescriptor => {
    const safeTab: NavTabId = activeTab === 'trading' ? 'screening' : (activeTab as NavTabId);
    return {
      v: 3,
      activeTab: safeTab,
      deepSubTab: safeTab === 'deep_analysis' ? deepSubTab : null,
      panel: panel.isOpen
        ? {
            ticker: panel.selectedTicker,
            tab: panel.activeTab,
            source: panel.source,
          }
        : null,
      returnTab: returnTab ?? null,
    };
  }, [activeTab, deepSubTab, panel.isOpen, panel.selectedTicker, panel.activeTab, panel.source, returnTab]);

  // 1a. Keep latestStateRef fresh every render - popstate listener reads through this.
  useEffect(() => {
    latestStateRef.current = { activeTab, deepSubTab, panel, returnTab, isAuthenticated, isAdmin };
  });

  // 1. Seed once - merge appNav into existing history.state so deep-link consumer doesn't clobber.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const desc = serialize();
    const prev = window.history.state ?? {};
    try {
      window.history.replaceState({ ...prev, appNav: desc }, '', window.location.href);
    } catch {
      // replaceState is non-critical; continue.
    }
    lastAppliedDescRef.current = desc;
    // Seed runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Push/replace on state change.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const curDesc = serialize();

    if (restoringRef.current) {
      if (descEquals(curDesc, restoringRef.current.targetDesc)) {
        restoringRef.current = null;
        lastAppliedDescRef.current = curDesc;
      }
      return;
    }

    if (activeTab === 'trading') return;

    if (descEquals(curDesc, lastAppliedDescRef.current)) return;

    const now = Date.now();
    const rapid = now - lastPushTimeRef.current < TICKER_SCRUB_REPLACE_MS;
    const onlyTicker = isOnlyTickerDiff(curDesc, lastAppliedDescRef.current);

    try {
      if (onlyTicker && rapid) {
        window.history.replaceState(
          { ...(window.history.state ?? {}), appNav: curDesc },
          '',
          window.location.href
        );
      } else {
        window.history.pushState(
          { ...(window.history.state ?? {}), appNav: curDesc },
          '',
          window.location.href
        );
        lastPushTimeRef.current = now;
      }
      lastAppliedDescRef.current = curDesc;
    } catch {
      // History API unavailable - silently drop; app continues to work.
    }
    // `serialize` is memoised on the same deps listed above; tracked explicitly
    // so lint can see it - function identity only changes when those deps change.
  }, [activeTab, deepSubTab, panel.isOpen, panel.selectedTicker, panel.activeTab, panel.source, returnTab, serialize]);

  // 3. popstate - restore from descriptor.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onPopState = (event: PopStateEvent) => {
      const raw = (event.state as { appNav?: unknown } | null)?.appNav;
      const desc = validateDescriptor(raw);
      if (!desc) return;

      const {
        activeTab: curTab,
        deepSubTab: curSubTab,
        panel: curPanel,
        returnTab: curReturn,
        isAuthenticated: auth,
        isAdmin: admin,
      } = latestStateRef.current;

      const safeTab = mapToSafeTab(desc.activeTab, { isAuthenticated: auth, isAdmin: admin });
      const finalDesc: NavDescriptor =
        safeTab === desc.activeTab ? desc : { ...desc, activeTab: safeTab };

      restoringRef.current = { targetDesc: finalDesc };

      const applyUpdates = () => {
        if (finalDesc.panel) {
          dispatch(
            openPanel({
              ticker: finalDesc.panel.ticker,
              tab: finalDesc.panel.tab,
              source: finalDesc.panel.source,
            })
          );
        } else if (curPanel.isOpen) {
          dispatch(closePanel());
        }

        if (finalDesc.returnTab !== curReturn) {
          if (finalDesc.returnTab) {
            dispatch(setDeepAnalysisReturnTab(finalDesc.returnTab));
          } else {
            dispatch(clearDeepAnalysisReturnTab());
          }
        }

        if (finalDesc.activeTab !== curTab) {
          setActiveTab(finalDesc.activeTab as TabId);
        }

        const nextSubTab: DeepSubTab = finalDesc.deepSubTab ?? 'analysis';
        if (nextSubTab !== curSubTab) {
          setDeepSubTab(nextSubTab);
        }
      };

      const batch: (cb: () => void) => void =
        (ReactDOM as unknown as { unstable_batchedUpdates?: (cb: () => void) => void })
          .unstable_batchedUpdates ?? ((cb: () => void) => cb());
      batch(applyUpdates);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
    // Listener registered once - reads through latestStateRef for fresh values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
